import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { usePopularSections } from './usePopularSections'
import { useBotsWidget } from '../context/BotsWidgetContext.tsx'
import { useProviders } from '../../../hooks/useProviders.ts'
import { useSelectedModel } from '../../../context/SelectedModelContext.tsx'
import {
	applyPopularItem,
	findTrendMemberHome,
	makeFindModel,
	pickTrendMember,
	rawCardToItem,
	type BackendCard,
} from '../utils/popularCardApply.ts'
import { getAttachedStyleCard } from '../../../api/attachedStyleCard.ts'
import { cardPageUrl, cardPrimaryUrl, cardVisibleOn, catalogPathOf, type ToolPage } from '../utils/cardSeo.ts'
import { rememberToolRun } from '../utils/activeToolRun'

export function useToolCardLanding(category: ToolPage, cardSlug: string | undefined) {
	const navigate = useNavigate()
	const { sections } = usePopularSections(category)
	const { handleStyleApply, handleExpand } = useBotsWidget()
	const { brands } = useProviders()
	const { selectModel } = useSelectedModel()
	const findModel = useMemo(() => makeFindModel(brands), [brands])

	// Все карточки пула страницы (без фильтра раздела) — чтобы старая ссылка на участника мульти-тренда нашла его
	// группу, даже если группа живёт в другом разделе; видимые на этом разделе — ниже.
	const allCards = useMemo(() => {
		const out: BackendCard[] = []
		for (const sec of sections ?? []) {
			const list = Array.isArray(sec.config.cards) ? (sec.config.cards as BackendCard[]) : []
			out.push(...list)
		}
		return out
	}, [sections])
	const cards = useMemo(() => allCards.filter((card) => cardVisibleOn(card, category)), [allCards, category])

	const card = useMemo(() => {
		if (!cardSlug) return null
		const norm = cardSlug.toLowerCase()
		return cards.find((c) => (c.slug || '').toLowerCase() === norm) ?? null
	}, [cards, cardSlug])

	const appliedIdRef = useRef<string | null>(null)
	// Мульти-тренд (22.10): клик по свотчу участника в виджете — та же страница с state.trendMemberId; применяем
	// группу заново с этим участником. Какой участник применён — своя защёлка, чтобы повторный клик по тому же
	// молчал, а смена участника при том же слаге проходила.
	const appliedMemberRef = useRef<string | null>(null)
	// Адрес страницы — из роутера, не из window.location: поверх может быть открыто окно входа (11.10), и тогда в
	// адресной строке /register, а «открытый инструмент» должен запомнить лендинг карточки.
	const location = useLocation()
	const landingPath = location.pathname
	const wantedMember = (location.state as { trendMemberId?: string } | null)?.trendMemberId ?? null
	useEffect(() => {
		if (!cardSlug) {
			// Слаг снят (крестик виджета срезал /tool/… из пути) — защёлка
			// сбрасывается, иначе ПОВТОРНЫЙ выбор той же карточки молча
			// пропускался бы (id совпал бы с последним применённым).
			appliedIdRef.current = null
			appliedMemberRef.current = null
			return
		}
		if (sections === null) return
		if (!card) {
			// Ссылка на тренд, спрятанный в мульти-тренд (его плитки и страницы больше нет): открываем группу с ним.
			const home = findTrendMemberHome(allCards, cardSlug)
			const homeUrl = home ? cardPageUrl(home.group, category) ?? cardPrimaryUrl(home.group) : null
			if (home && homeUrl) {
				navigate(homeUrl, { replace: true, state: { trendMemberId: home.memberId, skipAnchor: true } })
				return
			}
			navigate(catalogPathOf(category), { replace: true })
			return
		}
		if (brands.length === 0) return
		const memberKey = card.card_kind === 'multitrend' ? wantedMember ?? '' : null
		if (appliedIdRef.current === card.id && appliedMemberRef.current === memberKey) return
		appliedIdRef.current = card.id
		appliedMemberRef.current = memberKey
		// Пайплайн-карточка: запоминаем ЭТОТ лендинг как «открытый инструмент»
		// раздела. Возврат в раздел с незакрытым job'ом вернёт юзера сюда же
		// (см. activeToolRun) — работа не теряется при уходе на другую страницу.
		// Мульти-тренд: в виджет уходит участник — запись о прогоне ведём по НЕМУ (у группы слаг главного).
		const applied = card.card_kind === 'multitrend' ? pickTrendMember(rawCardToItem(card), wantedMember, getAttachedStyleCard()) : null
		const pipelineSlug = (applied ?? card).pipeline_slug
		if (pipelineSlug) {
			rememberToolRun({
				page: category,
				path: landingPath,
				pipelineSlug,
			})
		}
		applyPopularItem(rawCardToItem(card), undefined, {
			category,
			findModel,
			handleStyleApply,
			selectModel,
			navigate,
			// Группа трендов: участник по клику в виджете (state), иначе из пина/главный; лендинг группы — для возврата.
			trendMemberId: wantedMember,
			trendGroupPath: landingPath,
		})
	}, [cardSlug, sections, card, allCards, brands, category, findModel, handleStyleApply, selectModel, navigate, landingPath, wantedMember])

	// Повторный клик по той же карточке: адрес тот же, слаг не меняется — эффект выше молчит, а карточка уже в виджете.
	// Клик плитки — новая навигация (новый location.key): раскрываем свёрнутый виджет (владелец 29.09: сворачивание
	// больше не снимает карточку — вернуться к ней тем же кликом).
	const landedKeyRef = useRef<string | null>(null)
	useEffect(() => {
		if (!card || appliedIdRef.current !== card.id) {
			landedKeyRef.current = null
			return
		}
		if (landedKeyRef.current === null) {
			landedKeyRef.current = location.key
			return
		}
		if (landedKeyRef.current !== location.key) {
			landedKeyRef.current = location.key
			handleExpand()
		}
	}, [card, location.key, handleExpand])

	// Якорь к самой карточке: лендинг показывает не верх страницы, а плитку
	// инструмента — скролл к ней (центр вьюпорта) + короткая подсветка-glow.
	// ТОЛЬКО для пришедших ПО ССЫЛКЕ (прямой заход/шара): клик по карточке на
	// самой витрине передаёт state.skipAnchor — юзер уже смотрит на плитку,
	// авто-скролл дёргал бы страницу. Плитки несут data-card-slug; сетка
	// рендерится после загрузки секций — ретраим поиск узла до ~2с.
	const anchoredRef = useRef<string | null>(null)
	useEffect(() => {
		const slug = card?.slug
		if (!slug || anchoredRef.current === slug) return
		anchoredRef.current = slug
		if ((location.state as { skipAnchor?: boolean } | null)?.skipAnchor) return
		let tries = 0
		let timer: number | undefined
		const tryScroll = () => {
			const el = document.querySelector(`[data-card-slug="${CSS.escape(slug)}"]`)
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' })
				el.classList.add('card-anchor-glow')
				timer = window.setTimeout(() => el.classList.remove('card-anchor-glow'), 2200)
			} else if (++tries < 10) {
				timer = window.setTimeout(tryScroll, 200)
			}
		}
		timer = window.setTimeout(tryScroll, 150)
		return () => window.clearTimeout(timer)
	}, [card, location.state])

	const cardHasDemo = useMemo(() => {
		if (!card) return false
		if (card.demo_type === 'video') return !!card.video_url
		return !!card.demo_before_url && !!card.demo_variants?.length
	}, [card])

	const similar = useMemo(() => {
		if (!card) return []
		return cards.filter((c) => c.id !== card.id && !!c.slug && !!cardPrimaryUrl(c)).slice(0, 8)
	}, [cards, card])

	return { card, similar, cardHasDemo }
}
